import {computed, type ComputedRef} from 'vue';
import {storeToRefs} from 'pinia';
import {useUserStore} from '@/ts/store/user-store';
import {useLang} from '@/ts/composables/stateful/use-lang';
import {useDateFormat, type DateFormatComposable} from '@/ts/composables/pure/use-date-format';

/**
 * The reader's own date patterns: the country from their profile, the language they read in
 * (IPD-3229).
 *
 * The store is read here rather than in each component so the patterns reach an atom without being
 * drilled through everything between, the way `formatted-date` already took its language from the
 * app rather than from a prop.
 *
 * Absent a country — a public page, or a profile still in flight — the day-first form stands in.
 * Nothing renders in that window today: every consumer sits inside the `loading-error` wrapper in
 * `main-base-page`, which waits for the profile call before it draws the route.
 */
export function useProfileDateFormat(): ComputedRef<DateFormatComposable> {
  const {userData} = storeToRefs(useUserStore());
  const {lang} = useLang();

  return computed(() => useDateFormat({
    countryCode: userData.value?.countryCode,
    language: lang.value,
  }));
}
